import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColumns, GridRow as DataGridRow, GridSortModel } from '@mui/x-data-grid';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { getEuImportHistory } from '../../../server/server';
import DateSearchHeader from '../../../common/DateSearchHeader';

interface ImportHistoryListProps {
  onRowClick: (row: any) => void;
  refreshKey?: number;
}

function ImportHistoryList({ onRowClick, refreshKey }: ImportHistoryListProps) {
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [totalTableRows, setTotalTableRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'date', sort: 'desc' },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  // Search/filter state
  const [searchFilters, setSearchFilters] = useState({
    date: '',
    groupSetName: '',
    startedBy: '',
    addedRows: '',
    updatedRows: '',
  });

  const buildSearchParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        params[key] = value.trim();
      }
    });
    return params;
  };

  const loadHistory = useCallback(async (
    offset: number = 0,
    limit: number = 25,
    sort: string = 'date',
    order: string = 'DESC',
    searchParams?: Record<string, string>,
  ) => {
    setIsLoading(true);
    try {
      const resp = await getEuImportHistory(limit, offset, sort, order, searchParams);
      if (resp?.status === 200) {
        setTableRows(resp.data.rows);
        setTotalTableRows(resp.data.totalRows);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  // Debounced auto-search for text fields
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setPage(0);
        const sortField = sortModel[0]?.field ?? 'date';
        const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'DESC';
        const params: Record<string, string> = {};
        Object.entries(next).forEach(([k, v]) => {
          if (v && v.trim() !== '') params[k] = v.trim();
        });
        setIsLoading(true);
        getEuImportHistory(pageSize, 0, sortField, sortOrder, params).then((resp) => {
          if (resp?.status === 200) {
            setTableRows(resp.data.rows);
            setTotalTableRows(resp.data.totalRows);
            setIsLoading(false);
          }
        });
      }, 300);
      return next;
    });
  };

  const handleDateSearch = (field: string, dateStr: string) => {
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: dateStr };
      setPage(0);
      const sortField = sortModel[0]?.field ?? 'date';
      const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'DESC';
      const params: Record<string, string> = {};
      Object.entries(next).forEach(([k, v]) => {
        if (v && v.trim() !== '') params[k] = v.trim();
      });
      setIsLoading(true);
      getEuImportHistory(pageSize, 0, sortField, sortOrder, params).then((resp) => {
        if (resp?.status === 200) {
          setTableRows(resp.data.rows);
          setTotalTableRows(resp.data.totalRows);
          setIsLoading(false);
        }
      });
      return next;
    });
  };

  const renderSearchHeader = (title: string, field: string, type: 'text' | 'date' = 'text') => {
    if (type === 'date') {
      return (
        <DateSearchHeader
          title={title}
          value={searchFilters[field as keyof typeof searchFilters] ?? ''}
          onChange={(dateStr) => handleDateSearch(field, dateStr)}
        />
      );
    }
    return (
      <div className="flex flex-col w-full" style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <span className="text-xs font-semibold mb-1">{title}</span>
        <TextField
          size="small"
          fullWidth
          value={searchFilters[field as keyof typeof searchFilters] ?? ''}
          onChange={(e) => handleSearchChange(field, e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Претрага..."
          variant="standard"
          sx={{
            width: '100%',
            '& .MuiInput-root': { width: '100%' },
            '& .MuiInput-input': { fontSize: '0.75rem', padding: '2px 0' },
            '& .MuiInput-underline:before': { borderBottomColor: '#81848f3d' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#1D4B8E' },
            '& .MuiInput-underline:after': { borderBottomColor: '#1D4B8E' },
          }}
        />
      </div>
    );
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    if (newModel.length === 0) {
      const prevField = sortModel[0]?.field ?? 'date';
      const prevSort: 'asc' | 'desc' = sortModel[0]?.sort === 'asc' ? 'desc' : 'asc';
      const toggled = [{ field: prevField, sort: prevSort }];
      setSortModel(toggled);
      loadHistory(page * pageSize, pageSize, prevField, prevSort.toUpperCase(), buildSearchParams());
      return;
    }
    setSortModel(newModel);
    const { field, sort } = newModel[0];
    loadHistory(page * pageSize, pageSize, field, sort?.toUpperCase() ?? 'DESC', buildSearchParams());
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const sortField = sortModel[0]?.field ?? 'date';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'DESC';
    loadHistory(newPage * pageSize, pageSize, sortField, sortOrder, buildSearchParams());
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    const sortField = sortModel[0]?.field ?? 'date';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'DESC';
    loadHistory(0, newPageSize, sortField, sortOrder, buildSearchParams());
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const columns: GridColumns<any> = [
    {
      field: 'date',
      headerName: '',
      minWidth: 150,
      flex: 0.6,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум увоза', 'date', 'date'),
      renderCell: (params) => {
        if (!params.value) return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy. HH:mm') : '/';
      },
    },
    {
      field: 'groupSetName',
      headerName: '',
      minWidth: 140,
      flex: 0.6,
      sortable: false,
      renderHeader: () => renderSearchHeader('Група', 'groupSetName'),
      renderCell: (params) => params.value || '/',
    },
    {
      field: 'startedBy',
      headerName: '',
      minWidth: 130,
      flex: 0.5,
      sortable: false,
      renderHeader: () => renderSearchHeader('Корисник', 'startedBy'),
      renderCell: (params) => params.value || '/',
    },
    {
      field: 'addedRows',
      headerName: '',
      minWidth: 90,
      flex: 0.35,
      sortable: true,
      renderHeader: () => renderSearchHeader('Додато', 'addedRows'),
      renderCell: (params) => (
        <span className="text-green-700 font-medium">{(params.value ?? 0).toLocaleString()}</span>
      ),
    },
    {
      field: 'updatedRows',
      headerName: '',
      minWidth: 90,
      flex: 0.35,
      sortable: true,
      renderHeader: () => renderSearchHeader('Измењено', 'updatedRows'),
      renderCell: (params) => (
        <span className="text-blue-700 font-medium">{(params.value ?? 0).toLocaleString()}</span>
      ),
    },
  ];

  function CustomGridFooter(props: any) {
    const totalPages = Math.ceil(props.count / props.pageSize);
    const handlePageSizeChange = (event: any) => {
      props.onPageSizeChange(event.target.value);
    };
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px',
        }}
      >
        <FormControl>
          <InputLabel htmlFor="history-rows-per-page">Редова по страници</InputLabel>
          <Select
            value={props.pageSize}
            size="small"
            label="Редова по страници"
            sx={{ minWidth: 170 }}
            onChange={handlePageSizeChange}
            inputProps={{
              name: 'history-rows-per-page',
              id: 'history-rows-per-page',
            }}
          >
            {props.rowsPerPageOptions.map((option: any) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <p className="text-[#04438b]">Укупно редова: {props.count}</p>
        <Pagination
          shape="rounded"
          count={totalPages}
          page={props.page + 1}
          onChange={(event, newPage) => props.onPageChange(event, newPage - 1)}
          siblingCount={1}
          boundaryCount={1}
        />
      </div>
    );
  }

  return (
    <div ref={tableRef}>
      <h2 className="text-[#04438b] text-2xl font-bold mb-4">
        Историјат увоза
      </h2>
      <DataGrid
        rows={tableRows}
        columns={columns}
        loading={isLoading}
        rowCount={totalTableRows}
        pagination
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        page={page}
        pageSize={pageSize}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        style={{ zIndex: 0 }}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        rowHeight={60}
        autoHeight={true}
        sx={{
          '& .MuiDataGrid-columnHeader': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            overflow: 'visible',
            width: '100%',
          },
          '& .MuiDataGrid-columnHeaderWrapper': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            paddingTop: '8px',
            paddingBottom: '8px',
            minHeight: '60px !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            borderLeft: '3px solid transparent',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
            borderLeft: '3px solid #1D4B8E',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
            borderLeft: '3px solid #1D4B8E',
          },
        }}
        getRowId={(row) => row.id}
        onRowClick={(params) => onRowClick(params.row)}
        components={{
          Row: (props: any) => (
            <Tooltip title="Кликните за детаље" placement="left" arrow followCursor>
              <span style={{ display: 'contents' }}>
                {/* @ts-ignore */}
                <DataGridRow {...props} />
              </span>
            </Tooltip>
          ),
          Footer: CustomGridFooter,
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Нема података о увозима
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Нема података о увозима
            </Stack>
          ),
        }}
        componentsProps={{
          footer: {
            count: totalTableRows,
            page: page,
            pageSize: pageSize,
            onPageChange: (event: any, newPage: any) => handlePageChange(newPage),
            onPageSizeChange: (newPageSize: any) => handlePageSizeChange(newPageSize),
            rowsPerPageOptions: [10, 25, 50, 100],
          },
        }}
      />
    </div>
  );
}

export default ImportHistoryList;
